import React from 'react';
import { Activity, Clock3, Layers3, ListOrdered, Server } from 'lucide-react';
import { Navigate } from 'react-router-dom';

import { useAuth } from '../../../contexts/AuthContext';
import { isAdminUser } from '../../../utils/api';
import { useTasksOverview } from '../../../hooks/useAdminBackfill';
import { Heading } from '../../ui/heading';
import { Badge } from '../../ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../ui/card';

const TaskQueueSettingsPage: React.FC = () => {
  const { user } = useAuth();
  const { data, isLoading, error } = useTasksOverview();

  if (!isAdminUser(user)) {
    return <Navigate to="/v4/records" replace />;
  }

  const totalInFlight =
    (data?.tasks.active ?? 0) +
    (data?.tasks.reserved ?? 0) +
    (data?.tasks.scheduled ?? 0);

  const activeTaskRows = Object.entries(data?.active_tasks_by_worker ?? {});
  const queueRows =
    data?.queues && !('error' in data.queues)
      ? Object.entries(data.queues).sort((a, b) => Number(b[1]) - Number(a[1]))
      : [];

  return (
    <div className="h-full px-4 py-5 md:px-6 md:py-6">
      <div className="mb-6">
        <Heading
          level={2}
          className="font-jakarta text-2xl font-extrabold tracking-[-0.02em] text-foreground"
        >
          Task queue
        </Heading>
        <p className="mt-2 max-w-2xl text-[13px] text-muted-foreground">
          Live Celery worker and queue visibility for imports, classification, and scheduled
          background processing. This view refreshes automatically every 5 seconds.
        </p>
      </div>

      {error ? (
        <Card className="border-rose-200 bg-rose-50 shadow-none">
          <CardContent className="p-4 text-sm text-rose-700">
            {error instanceof Error ? error.message : 'Failed to load queue status.'}
          </CardContent>
        </Card>
      ) : null}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
        <Card className="border-slate-200/80 shadow-sm">
          <CardHeader className="gap-2">
            <CardDescription>Total in flight</CardDescription>
            <CardTitle className="font-jakarta text-3xl tracking-[-0.03em]">
              {isLoading ? '...' : totalInFlight}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 text-sm text-muted-foreground">
            Active, reserved, and scheduled tasks combined.
          </CardContent>
        </Card>

        <Card className="border-slate-200/80 shadow-sm">
          <CardHeader className="gap-2">
            <CardDescription>Active workers</CardDescription>
            <CardTitle className="flex items-center gap-2 font-jakarta text-3xl tracking-[-0.03em]">
              <Server className="h-6 w-6 text-primary" />
              {isLoading ? '...' : data?.workers.count ?? 0}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 text-sm text-muted-foreground">
            Registered Celery worker nodes responding to inspection.
          </CardContent>
        </Card>

        <Card className="border-slate-200/80 shadow-sm">
          <CardHeader className="gap-2">
            <CardDescription>Queued</CardDescription>
            <CardTitle className="flex items-center gap-2 font-jakarta text-3xl tracking-[-0.03em]">
              <Layers3 className="h-6 w-6 text-primary" />
              {isLoading ? '...' : data?.tasks.queued ?? 0}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 text-sm text-muted-foreground">
            Redis queue depth across the known worker queues.
          </CardContent>
        </Card>

        <Card className="border-slate-200/80 shadow-sm">
          <CardHeader className="gap-2">
            <CardDescription>Scheduled</CardDescription>
            <CardTitle className="flex items-center gap-2 font-jakarta text-3xl tracking-[-0.03em]">
              <Clock3 className="h-6 w-6 text-primary" />
              {isLoading ? '...' : data?.tasks.scheduled ?? 0}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 text-sm text-muted-foreground">
            Tasks waiting on ETA or beat scheduling before execution.
          </CardContent>
        </Card>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 2xl:grid-cols-[minmax(0,1fr)_minmax(340px,0.9fr)]">
        <Card className="border-slate-200/80 shadow-sm">
          <CardHeader className="gap-2 border-b border-slate-100">
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-primary" />
              Worker task state
            </CardTitle>
            <CardDescription>
              High-level queue state across all live workers.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 p-6 md:grid-cols-3">
            {[
              { label: 'Active', value: data?.tasks.active ?? 0 },
              { label: 'Reserved', value: data?.tasks.reserved ?? 0 },
              { label: 'Scheduled', value: data?.tasks.scheduled ?? 0 },
            ].map((item) => (
              <div
                key={item.label}
                className="rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-4"
              >
                <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                  {item.label}
                </p>
                <p className="mt-2 font-jakarta text-3xl font-bold tracking-[-0.03em] text-foreground">
                  {isLoading ? '...' : item.value}
                </p>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="border-slate-200/80 shadow-sm">
          <CardHeader className="gap-2 border-b border-slate-100">
            <CardTitle className="flex items-center gap-2">
              <ListOrdered className="h-4 w-4 text-primary" />
              Queue lengths
            </CardTitle>
            <CardDescription>
              Redis-backed queue depth by named worker queue.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="px-6 py-8 text-sm text-muted-foreground">Loading queue lengths…</div>
            ) : queueRows.length === 0 ? (
              <div className="px-6 py-8 text-sm text-muted-foreground">
                No queue backlog. All known queues are currently empty.
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {queueRows.map(([queueName, count]) => (
                  <div
                    key={queueName}
                    className="flex items-center justify-between px-6 py-3 text-sm"
                  >
                    <span className="font-medium capitalize text-foreground">
                      {queueName.replace(/_/g, ' ')}
                    </span>
                    <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-700">
                      {String(count)}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6 border-slate-200/80 shadow-sm">
        <CardHeader className="gap-2 border-b border-slate-100">
          <CardTitle>Active tasks by worker</CardTitle>
          <CardDescription>
            Shows currently running tasks only. Empty workers are omitted here.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="px-6 py-8 text-sm text-muted-foreground">Loading active tasks…</div>
          ) : activeTaskRows.length === 0 ? (
            <div className="px-6 py-8 text-sm text-muted-foreground">
              No active tasks are currently executing.
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {activeTaskRows.map(([workerName, tasks]) => (
                <div key={workerName} className="px-6 py-4">
                  <p className="text-sm font-semibold text-foreground">{workerName}</p>
                  <div className="mt-3 space-y-2">
                    {tasks.map((task: { id?: string; name?: string; time_start?: number | null }) => (
                      <div
                        key={task.id ?? task.name}
                        className="rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3"
                      >
                        <p className="text-sm font-medium text-foreground">{task.name ?? 'Unknown task'}</p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {task.id ?? 'No task id'}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TaskQueueSettingsPage;
